"use client";
import { Typography } from "antd";

const { Text } = Typography;

export default function SecurityProductPreview({
  formData = {},

  // LOVs
  productCategoryOptions = [],
  instrumentTypeOptions = [],
  assetClassOptions = [],
  yesNoOptions = [],
  quotationTypeOptions = [],
  dayCountConventionOptions = [],
  holidayCalendarOptions = [],
  ifrsClassificationOptions = [],
  currencyOptions = [],
  interestStructureOptions = [],
  couponTypeOptions = [],
  assetGLAccounts = [],
  couponFrequencyOptions = [],
  incomeGlAccounts = [],
  accountingBasisOptions = [],
  amortisationTypeOptions = [],
  principalRepaymentTypeOptions = [],
  incomeRecognitionMethodOptions = [],

  bondCouponFrequencies = [],
  pricingMethods = [],
  yieldBasisOptions = [],
  cleanDirtyPriceFlagOptions = [],
  valuationCurveTypeOptions = [],

  settlementDateTypes = [],
  interestCalculationBasisOptions = [],
  businessDayAdjustmentOptions = [],
  maturityAdjustmentRule = [],
  accrualPostingFrequencyOptions = [],

  durationMethodOptions = [],
  creditRiskClassOptions = [],
  exposureTypeOptions = [],
  maturityEventOptions = [],
}) {
  /* ===================== FLATTEN FORM DATA ===================== */
  const flatData = {
    ...formData.productIdentity,
    ...formData.behaviourRules,
    ...formData.pricingLogic,
    ...formData.dayCountCalendarRules,
    ...formData.accountingIFRS,
    ...formData.riskRegulatoryBehaviour,
    ...formData.lifeCycleAndEvents,
    ...formData.limitsEligibilityRules,
  };

  /* ===================== HELPERS ===================== */
  const resolveValue = (value, options = []) => {
    if (value === null || value === undefined) return "-";

    // Boolean
    if (typeof value === "boolean") return value ? "Yes" : "No";

    // Array (multi-selects, GLs, etc.)
    if (Array.isArray(value)) {
      if (!value.length) return "-";

      return value
        .map((v) => {
          // Handle GL object format: { debitAccount, creditAccount }
          if (v?.debitAccount || v?.creditAccount) {
            const debit =
              v.debitAccount?.label ||
              v.debitAccount?.code ||
              v.debitAccount?.id;
            const credit =
              v.creditAccount?.label ||
              v.creditAccount?.code ||
              v.creditAccount?.id;
            return [debit, credit].filter(Boolean).join(" / ");
          }

          // Normal LOV mapping
          const found = options.find((o) => o.value === v || o.id === v);
          return (
            found?.label ?? (typeof v === "object" ? JSON.stringify(v) : v)
          );
        })
        .join(", ");
    }

    // Number mapped to LOV
    if (typeof value === "number") {
      const found = options.find((o) => o.value === value || o.id === value);
      return found?.label ?? value;
    }

    // Object fallback (for unexpected cases)
    if (typeof value === "object") {
      return value.label || value.text || JSON.stringify(value);
    }

    // String
    return value;
  };

  const resolveGL = (gl, options = []) => {
    if (!gl || (!gl.debitAccount && !gl.creditAccount)) return "-";
    const debit =
      options.find((o) => o.value === gl.debitAccount)?.label ??
      gl.debitAccount ??
      "-";
    const credit =
      options.find((o) => o.value === gl.creditAccount)?.label ??
      gl.creditAccount ??
      "-";
    return `Debit: ${debit} / Credit: ${credit}`;
  };

  const formatNumber = (v) =>
    v !== undefined && v !== null ? Number(v).toLocaleString() : "-";

  const renderRow = (name, value, index, total) => {
    const isLast = index === total - 1;
    return (
      <div
        key={name}
        style={{
          display: "flex",
          borderBottom: isLast ? "none" : "1px solid #f0f0f0",
        }}
      >
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            backgroundColor: "#f9f9f9",
            fontWeight: 500,
            borderRight: "1px solid #f0f0f0",
          }}
        >
          {name}
        </div>
        <div
          style={{
            padding: "16px 24px",
            width: "50%",
            fontWeight: 600,
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {value ?? "-"}
        </div>
      </div>
    );
  };

  /* ===================== SECTIONS ===================== */
  const sections = [
    // 1. Product Identification & Classification
    {
      title: "1. Product Identification & Classification",
      items: [
        { name: "Product Code", value: flatData.productCode },
        { name: "Product Name", value: flatData.productName },
        {
          name: "Product Category",
          value: resolveValue(flatData.productCategory, productCategoryOptions),
        },
        {
          name: "Instrument Type",
          value: resolveValue(flatData.instrumentType, instrumentTypeOptions),
        },
        {
          name: "Asset Class",
          value: resolveValue(flatData.assetClass, assetClassOptions),
        },
        {
          name: "Active",
          value: resolveValue(flatData.activeFlag),
        },
      ],
    },

    // 2. Economic Behaviour & Cashflow Rules
    {
      title: "2. Economic Behaviour & Cashflow Rules",
      items: [
        {
          name: "Interest Structure",
          value: resolveValue(
            flatData.interestStructure,
            interestStructureOptions,
          ),
        },
        {
          name: "Coupon Type",
          value: resolveValue(flatData.couponType, couponTypeOptions),
        },
        {
          name: "Coupon Frequency",
          value: resolveValue(flatData.couponFrequency, bondCouponFrequencies),
        },
        {
          name: "Amortisation Type",
          value: resolveValue(
            flatData.amortisationType,
            amortisationTypeOptions,
          ),
        },
        {
          name: "Principal Repayment Type",
          value: resolveValue(
            flatData.principalRepaymentType,
            principalRepaymentTypeOptions,
          ),
        },
        {
          name: "Income Recognition Method",
          value: resolveValue(
            flatData.incomeRecognitionMethod,
            incomeRecognitionMethodOptions,
          ),
        },
        {
          name: "Accrual Required",
          value: resolveValue(flatData.accrualRequiredFlag),
        },
        {
          name: "Accrual Frequency",
          value: resolveValue(
            flatData.accrualFrequency,
            accrualPostingFrequencyOptions,
          ),
        },
      ],
    },

    // 3. Pricing & Yield Configuration
    {
      title: "3. Pricing & Yield Configuration",
      items: [
        {
          name: "Pricing Method",
          value: resolveValue(flatData.pricingMethod, pricingMethods),
        },
        {
          name: "Quotation Type",
          value: resolveValue(flatData.quotationType, quotationTypeOptions),
        },
        {
          name: "Yield Basis",
          value: resolveValue(flatData.yieldBasis, yieldBasisOptions),
        },
        {
          name: "Discount Basis",
          value: resolveValue(flatData.discountBasis, bondCouponFrequencies),
        },
        { name: "Price Decimals", value: flatData.priceDecimals },
        { name: "Yield Decimals", value: flatData.yieldDecimals },
        {
          name: "Clean / Dirty Price",
          value: resolveValue(
            flatData.cleanDirtyPriceFlag,
            cleanDirtyPriceFlagOptions,
          ),
        },
        {
          name: "Valuation Curve Type",
          value: resolveValue(
            flatData.valuationCurveType,
            valuationCurveTypeOptions,
          ),
        },
      ],
    },

    // 4. Day Count & Calendar Rules
    {
      title: "4. Day Count & Calendar Rules",
      items: [
        {
          name: "Day Count Convention",
          value: resolveValue(
            flatData.dayCountConvention,
            interestCalculationBasisOptions,
          ),
        },
        {
          name: "Business Day Convention",
          value: resolveValue(
            flatData.businessDayConvention,
            businessDayAdjustmentOptions,
          ),
        },
        {
          name: "Holiday Calendar",
          value: resolveValue(
            flatData.holidayCalendarCode,
            holidayCalendarOptions,
          ),
        },
        {
          name: "Settlement Days",
          value: resolveValue(flatData.settlementDays, settlementDateTypes),
        },
        {
          name: "Maturity Adjustment Rule",
          value: resolveValue(
            flatData.maturityAdjustmentRule,
            maturityAdjustmentRule,
          ),
        },
      ],
    },

    // 5. Accounting & IFRS Behaviour
    {
      title: "5. Accounting & IFRS Behaviour",
      items: [
        {
          name: "IFRS Classification",
          value: resolveValue(
            flatData.ifrsClassification,
            ifrsClassificationOptions,
          ),
        },
        {
          name: "Accounting Basis",
          value: resolveValue(flatData.accountingBasis, accountingBasisOptions),
        },
        {
          name: "Revaluation Required",
          value: resolveValue(flatData.revaluationRequiredFlag),
        },
        {
          name: "Revaluation Frequency",
          value: resolveValue(
            flatData.revaluationFrequency,
            accrualPostingFrequencyOptions,
          ),
        },
        {
          name: "Principal GL",
          value: resolveGL(flatData.principalGL, [
            ...incomeGlAccounts,
            ...assetGLAccounts,
          ]),
        },
        {
          name: "Accrued Interest GL",
          value: resolveGL(flatData.accruedInterestGL, incomeGlAccounts),
        },
        {
          name: "Interest Income GL",
          value: resolveGL(flatData.interestIncomeGL, incomeGlAccounts),
        },
        {
          name: "Unrealised Income GL",
          value: resolveGL(flatData.unrealisedIncomeGL, incomeGlAccounts),
        },
      ],
    },

    // 6. Risk & Regulatory Attributes
    {
      title: "6. Risk & Regulatory Attributes",
      items: [
        { name: "Risk Weight (%)", value: flatData.riskWeight },
        {
          name: "Duration Method",
          value: resolveValue(flatData.durationMethod, durationMethodOptions),
        },
        {
          name: "Exposure Type",
          value: resolveValue(flatData.exposureType, exposureTypeOptions),
        },
        {
          name: "Eligible for SLR",
          value: resolveValue(flatData.eligibleForSlrFlag, yesNoOptions),
        },
        {
          name: "Eligible for LCR",
          value: resolveValue(flatData.eligibleForLcrFlag, yesNoOptions),
        },
        { name: "Haircut Percentage (%)", value: flatData.haircutPercentage },
        {
          name: "Credit Risk Class",
          value: resolveValue(flatData.creditRiskClass, creditRiskClassOptions),
        },
      ],
    },

    // 7. Lifecycle & Event Controls
    {
      title: "7. Lifecycle & Event Controls",
      items: [
        {
          name: "Early Sale Allowed",
          value: resolveValue(flatData.earlySaleAllowed, yesNoOptions),
        },
        {
          name: "Rollover Allowed",
          value: resolveValue(flatData.rolloverAllowed, yesNoOptions),
        },
        {
          name: "Auto Maturity",
          value: resolveValue(flatData.autoMaturityFlag, yesNoOptions),
        },
        {
          name: "Maturity Event Action",
          value: resolveValue(flatData.maturityEventCode, maturityEventOptions),
        },
        {
          name: "Penalty Applicable",
          value: resolveValue(flatData.penaltyApplicableFlag, yesNoOptions),
        },
      ],
    },

    // 8. Limits & Eligibility Rules
    {
      title: "8. Limits & Eligibility Rules",
      items: [
        {
          name: "Counterparty Limit Required",
          value: resolveValue(flatData.counterpartyLimitRequired, yesNoOptions),
        },
        {
          name: "Portfolio Limit Required",
          value: resolveValue(flatData.portfolioLimitRequired, yesNoOptions),
        },
        {
          name: "Minimum Deal Size",
          value: formatNumber(flatData.dealSizeMin),
        },
        {
          name: "Maximum Deal Size",
          value: formatNumber(flatData.dealSizeMax),
        },
        {
          name: "Currency Restriction",
          value: resolveValue(
            Array.isArray(flatData.currencyRestriction)
              ? flatData.currencyRestriction
              : [flatData.currencyRestriction],
            currencyOptions,
          ),
        },
      ],
    },
  ];

  /* ===================== RENDER ===================== */
  return (
    <div style={{ width: "100%", padding: "0 24px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: "24px 0 8px" }}>
        Preview
      </h1>

      {sections.map((section, i) => (
        <div key={i}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "24px 0 16px" }}>
            {section.title}
          </h2>

          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              overflow: "hidden",
              marginBottom: 24,
            }}
          >
            {section.items.map((item, idx) =>
              renderRow(item.name, item.value, idx, section.items.length),
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
